// enums
import { RosterPlanModeEnum } from '../RosterPlan.enums';

// types
import type {
  RosterPlanModeGrants,
  RosterPlanSwitchableMode
} from '../RosterPlan.types';

/**
 * Whether the planner may switch to plan mode. Mirrors Dojo's `hasPlanModusFeature`, which the
 * roster widget ORs from the employee and request halves: a request manager may plan the open
 * requests without the roster plan grant (`views/roster/roster.js`, `RosterWidget.js:481`).
 */
const hasRosterPlanModeAccess = ({
  hasRosterPortalPlanModusAccess,
  hasRosterPortalRequestManagerAccess
}: RosterPlanModeGrants) =>
  hasRosterPortalPlanModusAccess || hasRosterPortalRequestManagerAccess;

/**
 * The modes the mode switch offers, in the Dojo selector's order (`RosterTopBar.renderModeSelector`):
 * view always, plan and changes after publication each behind their own grant.
 */
export const getAvailableRosterPlanModes = (
  grants: RosterPlanModeGrants
): RosterPlanSwitchableMode[] => [
  RosterPlanModeEnum.View,
  ...(hasRosterPlanModeAccess(grants)
    ? [RosterPlanModeEnum.Plan as const]
    : []),
  ...(grants.hasChangesAfterPublicationAccess
    ? [RosterPlanModeEnum.ChangesAfterPublication as const]
    : [])
];

/**
 * The mode the screen opens on: plan when the planner may plan, view otherwise — the Dojo selector's
 * `valueProp`. Not the store default, which is view for every planner until this is applied.
 */
export const getInitialRosterPlanMode = (
  grants: RosterPlanModeGrants
): RosterPlanSwitchableMode =>
  hasRosterPlanModeAccess(grants)
    ? RosterPlanModeEnum.Plan
    : RosterPlanModeEnum.View;
